import React from 'react'
import { toast, ToastContainer } from 'react-toastify'

import { ArtworkEditor } from '@domain-group/artwork-builder'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-light-svg-icons'

import '@domain-group/artwork-builder/dist/styles.css'

import useArtworkBuilder from '@hooks/useArtworkBuilder'

import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useRef } from 'react'
import { setCookie, getCookie, deleteCookie} from '@functions/cookie'

const CloseButton = ({ closeToast }) => (
  <a onClick={closeToast}>
    <FontAwesomeIcon icon={faTimes} />
  </a>
)

const selectSavedArtworkPage = async (savedArtworkPage, createFn, loadShortcodeDataFn, selectArtworkPageId, requestOptions, setState) => {
  const { artworkConfig, ...options } = requestOptions || {}
  const { campaigntrackOrderItemId } = artworkConfig || {}

  const shortcodeDataResult = await loadShortcodeDataFn({ id: campaigntrackOrderItemId })
  const { data: customShortcodeData } = shortcodeDataResult

  const mergedOptions = {
    ...options,
    custom_data: customShortcodeData,
  }

  createFn(savedArtworkPage, mergedOptions).then((response) => {
    const { success, data, errors } = response
    if (!success && errors){
      toast.warn(errors[0])
      return
    }

    selectArtworkPageId(data.id)

    setState({ showSelectTemplateModal: false })
  })

  window.newrelic?.setCustomAttribute("RealhubOrderItemID", campaigntrackOrderItemId, true)
  window.newrelic?.setCustomAttribute("RealhubArtworkID", selectArtworkPageId, true)
  window.newrelic?.setCustomAttribute("RealhubTemplate", templatePage, true)
}

const selectTemplatePage = async (templatePage, createFn, loadShortcodeDataFn, selectArtworkPageId, requestOptions, setState) => {
  const { artworkConfig, ...options } = requestOptions || {}
  const { campaigntrackOrderItemId } = artworkConfig || {}

  const shortcodeDataResult = await loadShortcodeDataFn({ id: campaigntrackOrderItemId })
  const { data: customShortcodeData } = shortcodeDataResult

  const mergedOptions = {
    ...options,
    custom_data: customShortcodeData,
  }

  createFn(templatePage, mergedOptions).then((response) => {
    const { success, data, errors } = response
    if (!success && errors){
      toast.warn(errors[0])
      return
    }

    selectArtworkPageId(data.id)

    setState({ showSelectTemplateModal: false })
  })

  window.newrelic?.setCustomAttribute("RealhubOrderItemID", campaigntrackOrderItemId, true)
  window.newrelic?.setCustomAttribute("RealhubArtworkID", selectArtworkPageId, true)
  window.newrelic?.setCustomAttribute("RealhubTemplate", templatePage, true)
}

const replaceArtworkPageTemplatePage = async (
  artworkPage,
  templatePage,
  updateFn,
  loadShortcodeDataFn,
  requestOptions,
  setState,
) => {
  const { artworkConfig, ...options } = requestOptions || {}
  const { campaigntrackOrderItemId } = artworkConfig || {}

  const shortcodeDataResult = await loadShortcodeDataFn({ id: campaigntrackOrderItemId })
  const { data: customShortcodeData } = shortcodeDataResult

  const mergedOptions = {
    ...options,
    custom_data: customShortcodeData,
  }

  updateFn(artworkPage, templatePage, mergedOptions).then((response) => {
    const { success, errors } = response
    if (!success && errors){
      toast.warn(errors[0])
      return
    }

    setState({ showSelectTemplateModal: false })
  })

  window.newrelic?.setCustomAttribute("RealhubOrderItemID", campaigntrackOrderItemId, true)
  window.newrelic?.setCustomAttribute("RealhubArtworkID", artworkPage, true)
  window.newrelic?.setCustomAttribute("RealhubTemplate", templatePage, true)
}

const EditPage = () => {
  const {
    callbacks: {
      deleteArtwork, fetchEditorOptions, goBack, loadShortcodeData,
    },
  } = useArtworkBuilder()

  const { user } = useAuth0()

  const currentEmail = user.email 
  const currentUserSub = user.sub

  const lastEmail = getCookie('lastEmail')
  const lastUserSub = getCookie('lastUserSub')

  const lastAuthenticatedUser = getCookie('lastAuthenticatedUser')
  const loginAttemptUser = getCookie('authenticatedUser')

  setCookie('currentEmail', currentEmail, 86400)
  setCookie('currentUserSub', currentUserSub, 86400)

  const userLoggedOut = getCookie('userLoggedOut')
  const logged = useRef(false)

  const loginFlowId = useRef(Math.random()).current

  useEffect(() => {
    window.newrelic?.addPageAction('Edit Page', {
      loginAttemptUser,
      lastUserSub,
      currentUserSub,
      loginFlowId,
      path: window.location.pathname,
      referrer: document.referrer,
      navigationType: performance.getEntriesByType('navigation')[0]?.type,
      logCreatedCookie: String(getCookie('logCreated')),
      userLoggedOutCookie: String(getCookie('userLoggedOut'))
    })

    if (logged.current) return

    const logCreated = getCookie('logCreated')
    if (logCreated === 'true') return

    logged.current = true
    setCookie('logCreated', 'true', 86400)
    window.newrelic?.setCustomAttribute("RealhubUsername", loginAttemptUser, true)
    
    if (lastUserSub === currentUserSub &&
        userLoggedOut === 'true'){
      const error = new Error('Auth0 stale session reuse')
      
      window.newrelic?.noticeError(error, {
          lastEmail,
          currentEmail,
          lastUserSub,
          currentUserSub,
          lastAuthenticatedUser,
          loginAttemptUser,
          type: 'AUTH0_SESSION_REUSE',
        })
    }
    else {
      window.newrelic?.addPageAction('Login Success', {
          lastEmail,
          currentEmail,
          lastUserSub,
          currentUserSub,
          lastAuthenticatedUser,
          loginAttemptUser,
          loginFlowId,
          path: window.location.pathname,
          referrer: document.referrer,
          navigationType: performance.getEntriesByType('navigation')[0]?.type,
          logCreatedCookie: String(getCookie('logCreated')),
          userLoggedOutCookie: String(getCookie('userLoggedOut')),
          page: 'Edit Page'
        }
      )
    }

    deleteCookie('userLoggedOut')
  }, [currentUserSub])

  return (
    <ArtworkEditor
      callbacks={{
        deleteArtwork,
        fetchEditorOptions,
        goBack,
        replaceArtworkPageTemplatePage: (artworkPage, templatePage, updateFn, requestOptions, setState) => replaceArtworkPageTemplatePage(artworkPage, templatePage, updateFn, loadShortcodeData, requestOptions, setState),
        selectSavedArtworkPage: (savedArtworkPage, createFn, selectArtworkPageId, requestOptions, setState) => selectSavedArtworkPage(savedArtworkPage, createFn, loadShortcodeData, selectArtworkPageId, requestOptions, setState),
        selectTemplatePage: (templatePage, createFn, selectArtworkPageId, requestOptions, setState) => selectTemplatePage(templatePage, createFn, loadShortcodeData, selectArtworkPageId, requestOptions, setState),
      }}
    >
      <ArtworkEditor.Header />
      <ArtworkEditor.Canvas />

      <ToastContainer closeButton={<CloseButton />} hideProgressBar autoClose={5000} />
    </ArtworkEditor>
  )
}

export default EditPage
